import { oc } from "@orpc/contract";
import { z } from "zod";

const UUIDSchema = z.uuid();

// ---------------------------------------------------------------------------
// Schemas
// ---------------------------------------------------------------------------

export const EmailTemplateVariableSchema = z.object({
  key: z.string(),
  label: z.string(),
  type: z.enum(["string", "number", "boolean", "array", "object"]).optional(),
  required: z.boolean().optional(),
  defaultValue: z.unknown().optional(),
  sampleValue: z.unknown().optional(),
});

export const EmailTemplateTypeEnum = z.enum(["html", "json_render"]);

export const EmailTemplateTypeSchema = EmailTemplateTypeEnum.default("html");

export const EmailTemplateVersionStatusSchema = z.enum(["draft", "finalized"]);

export const EmailTemplateSchema = z.object({
  id: UUIDSchema,
  organizationId: UUIDSchema,
  name: z.string(),
  subject: z.string(),
  htmlContent: z.string(),
  textContent: z.string().nullable(),
  variables: z.array(EmailTemplateVariableSchema),
  templateType: EmailTemplateTypeSchema,
  jsonSpec: z.record(z.string(), z.unknown()).nullable(),
  isDefault: z.boolean(),
  isActive: z.boolean(),
  pinnedVersionId: UUIDSchema.nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export const EmailTemplateVersionSchema = z.object({
  id: UUIDSchema,
  emailTemplateId: UUIDSchema,
  parentVersionId: UUIDSchema.nullable(),
  status: EmailTemplateVersionStatusSchema,
  version: z.number().int(),
  changelog: z.string().nullable(),
  createdBy: UUIDSchema,
  subject: z.string(),
  htmlContent: z.string(),
  textContent: z.string().nullable(),
  variables: z.array(EmailTemplateVariableSchema).nullable(),
  templateType: EmailTemplateTypeSchema,
  jsonSpec: z.record(z.string(), z.unknown()).nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

// ---------------------------------------------------------------------------
// List
// ---------------------------------------------------------------------------

export const ListEmailTemplatesInputSchema = z.object({});
export const ListEmailTemplatesResponseSchema = z.object({
  templates: z.array(EmailTemplateSchema),
});

// ---------------------------------------------------------------------------
// Get
// ---------------------------------------------------------------------------

export const GetEmailTemplateInputSchema = z.object({
  id: UUIDSchema,
});
export const GetEmailTemplateResponseSchema = z.object({
  template: EmailTemplateSchema,
});

// ---------------------------------------------------------------------------
// Create
// ---------------------------------------------------------------------------

export const CreateEmailTemplateInputSchema = z.object({
  name: z.string().min(1),
  subject: z.string().min(1),
  body: z.string().min(1),
  variables: z.array(EmailTemplateVariableSchema),
  isDefault: z.boolean().optional(),
  templateType: EmailTemplateTypeEnum.optional(),
  jsonSpec: z.record(z.string(), z.unknown()).nullable().optional(),
});
export const CreateEmailTemplateResponseSchema = z.object({
  template: EmailTemplateSchema,
});

// ---------------------------------------------------------------------------
// Update
// ---------------------------------------------------------------------------

export const UpdateEmailTemplateInputSchema = z.object({
  id: UUIDSchema,
  name: z.string().min(1).optional(),
  subject: z.string().min(1).optional(),
  body: z.string().min(1).optional(),
  // Optional so a lineage-only change (e.g. a rename) can be sent without
  // version fields — `EmailTemplateService.update` only creates a new version
  // when version fields are present.
  variables: z.array(EmailTemplateVariableSchema).optional(),
  isActive: z.boolean().optional(),
  templateType: EmailTemplateTypeEnum.optional(),
  jsonSpec: z.record(z.string(), z.unknown()).nullable().optional(),
});
export const UpdateEmailTemplateResponseSchema = z.object({
  template: EmailTemplateSchema,
});

// ---------------------------------------------------------------------------
// Delete
// ---------------------------------------------------------------------------

export const DeleteEmailTemplateInputSchema = z.object({
  id: UUIDSchema,
});
export const DeleteEmailTemplateResponseSchema = z.object({
  success: z.boolean(),
});

// ---------------------------------------------------------------------------
// Render Preview
// ---------------------------------------------------------------------------

export const RenderPreviewInputSchema = z.object({
  jsonSpec: z.record(z.string(), z.unknown()),
  variables: z.record(z.string(), z.unknown()),
});
export const RenderPreviewResponseSchema = z.object({
  html: z.string(),
  plainText: z.string(),
  metadata: z.object({
    resolvedBlocks: z.array(
      z.object({
        blockId: UUIDSchema,
        versionId: UUIDSchema,
        version: z.number().int(),
      }),
    ),
  }),
});

// ---------------------------------------------------------------------------
// Built-ins (in-code default templates)
// ---------------------------------------------------------------------------

export const BuiltinEmailTemplateSchema = z.object({
  /** Stable key; the selector/config reference is `builtin:<key>`. */
  key: z.string(),
  name: z.string(),
  category: z.enum([
    "approval_notification",
    "order_notification",
    "order_confirmation",
  ]),
  subject: z.string(),
  templateType: EmailTemplateTypeSchema,
  htmlContent: z.string().nullable(),
  jsonSpec: z.record(z.string(), z.unknown()).nullable(),
  variables: z.array(EmailTemplateVariableSchema),
  /** Sample state used to preview the built-in (its loop/scalar values). */
  sampleState: z.record(z.string(), z.unknown()),
});

export const ListBuiltinEmailTemplatesInputSchema = z.object({});
export const ListBuiltinEmailTemplatesResponseSchema = z.object({
  builtins: z.array(BuiltinEmailTemplateSchema),
});

export const CreateEmailTemplateFromBuiltinInputSchema = z.object({
  builtinKey: z.string().min(1),
  /** Optional name override; defaults to the built-in's display name. */
  name: z.string().min(1).optional(),
});
export const CreateEmailTemplateFromBuiltinResponseSchema = z.object({
  template: EmailTemplateSchema,
});

// ---------------------------------------------------------------------------
// List Versions
// ---------------------------------------------------------------------------

export const ListEmailTemplateVersionsInputSchema = z.object({
  emailTemplateId: UUIDSchema,
});
export const ListEmailTemplateVersionsResponseSchema = z.object({
  versions: z.array(EmailTemplateVersionSchema),
});

// ---------------------------------------------------------------------------
// Create Draft
// ---------------------------------------------------------------------------

export const CreateEmailTemplateDraftInputSchema = z.object({
  emailTemplateId: UUIDSchema,
  subject: z.string().optional(),
  body: z.string().optional(),
  variables: z.array(EmailTemplateVariableSchema).optional(),
  templateType: EmailTemplateTypeEnum.optional(),
  jsonSpec: z.record(z.string(), z.unknown()).nullable().optional(),
});
export const CreateEmailTemplateDraftResponseSchema = z.object({
  version: EmailTemplateVersionSchema,
});

// ---------------------------------------------------------------------------
// Update Draft
// ---------------------------------------------------------------------------

export const UpdateEmailTemplateDraftInputSchema = z.object({
  versionId: UUIDSchema,
  subject: z.string().optional(),
  body: z.string().optional(),
  variables: z.array(EmailTemplateVariableSchema).optional(),
  templateType: EmailTemplateTypeEnum.optional(),
  jsonSpec: z.record(z.string(), z.unknown()).nullable().optional(),
});
export const UpdateEmailTemplateDraftResponseSchema = z.object({
  version: EmailTemplateVersionSchema,
});

// ---------------------------------------------------------------------------
// Finalize Draft
// ---------------------------------------------------------------------------

export const FinalizeEmailTemplateDraftInputSchema = z.object({
  versionId: UUIDSchema,
});
export const FinalizeEmailTemplateDraftResponseSchema = z.object({
  version: EmailTemplateVersionSchema,
});

// ---------------------------------------------------------------------------
// Publish Version
// ---------------------------------------------------------------------------

export const PublishEmailTemplateVersionInputSchema = z.object({
  emailTemplateId: UUIDSchema,
  versionId: UUIDSchema,
});
export const PublishEmailTemplateVersionResponseSchema = z.object({
  template: EmailTemplateSchema,
});

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

export const emailTemplatesContracts = {
  list: oc
    .input(ListEmailTemplatesInputSchema)
    .output(ListEmailTemplatesResponseSchema)
    .route({ method: "GET" }),
  get: oc
    .input(GetEmailTemplateInputSchema)
    .output(GetEmailTemplateResponseSchema)
    .route({ method: "GET" }),
  create: oc
    .input(CreateEmailTemplateInputSchema)
    .output(CreateEmailTemplateResponseSchema)
    .route({ method: "POST" }),
  update: oc
    .input(UpdateEmailTemplateInputSchema)
    .output(UpdateEmailTemplateResponseSchema)
    .route({ method: "POST" }),
  delete: oc
    .input(DeleteEmailTemplateInputSchema)
    .output(DeleteEmailTemplateResponseSchema)
    .route({ method: "POST" }),
  renderPreview: oc
    .input(RenderPreviewInputSchema)
    .output(RenderPreviewResponseSchema)
    .route({ method: "POST" }),
  builtins: oc
    .input(ListBuiltinEmailTemplatesInputSchema)
    .output(ListBuiltinEmailTemplatesResponseSchema)
    .route({ method: "GET" }),
  createFromBuiltin: oc
    .input(CreateEmailTemplateFromBuiltinInputSchema)
    .output(CreateEmailTemplateFromBuiltinResponseSchema)
    .route({ method: "POST" }),
  listVersions: oc
    .input(ListEmailTemplateVersionsInputSchema)
    .output(ListEmailTemplateVersionsResponseSchema)
    .route({ method: "GET" }),
  createDraft: oc
    .input(CreateEmailTemplateDraftInputSchema)
    .output(CreateEmailTemplateDraftResponseSchema)
    .route({ method: "POST" }),
  updateDraft: oc
    .input(UpdateEmailTemplateDraftInputSchema)
    .output(UpdateEmailTemplateDraftResponseSchema)
    .route({ method: "POST" }),
  finalizeDraft: oc
    .input(FinalizeEmailTemplateDraftInputSchema)
    .output(FinalizeEmailTemplateDraftResponseSchema)
    .route({ method: "POST" }),
  publishVersion: oc
    .input(PublishEmailTemplateVersionInputSchema)
    .output(PublishEmailTemplateVersionResponseSchema)
    .route({ method: "POST" }),
} as const;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type EmailTemplateResponse = z.infer<typeof EmailTemplateSchema>;
export type EmailTemplateVariableResponse = z.infer<
  typeof EmailTemplateVariableSchema
>;
export type EmailTemplateVersionResponse = z.infer<
  typeof EmailTemplateVersionSchema
>;

export type ListEmailTemplatesInput = z.infer<
  typeof ListEmailTemplatesInputSchema
>;
export type ListEmailTemplatesResponse = z.infer<
  typeof ListEmailTemplatesResponseSchema
>;

export type GetEmailTemplateInput = z.infer<typeof GetEmailTemplateInputSchema>;
export type GetEmailTemplateResponse = z.infer<
  typeof GetEmailTemplateResponseSchema
>;

export type CreateEmailTemplateInput = z.infer<
  typeof CreateEmailTemplateInputSchema
>;
export type CreateEmailTemplateResponse = z.infer<
  typeof CreateEmailTemplateResponseSchema
>;

export type UpdateEmailTemplateInput = z.infer<
  typeof UpdateEmailTemplateInputSchema
>;
export type UpdateEmailTemplateResponse = z.infer<
  typeof UpdateEmailTemplateResponseSchema
>;

export type DeleteEmailTemplateInput = z.infer<
  typeof DeleteEmailTemplateInputSchema
>;
export type DeleteEmailTemplateResponse = z.infer<
  typeof DeleteEmailTemplateResponseSchema
>;

export type RenderPreviewInput = z.infer<typeof RenderPreviewInputSchema>;
export type RenderPreviewResponse = z.infer<typeof RenderPreviewResponseSchema>;

export type BuiltinEmailTemplate = z.infer<typeof BuiltinEmailTemplateSchema>;

export type ListBuiltinEmailTemplatesInput = z.infer<
  typeof ListBuiltinEmailTemplatesInputSchema
>;
export type ListBuiltinEmailTemplatesResponse = z.infer<
  typeof ListBuiltinEmailTemplatesResponseSchema
>;

export type CreateEmailTemplateFromBuiltinInput = z.infer<
  typeof CreateEmailTemplateFromBuiltinInputSchema
>;
export type CreateEmailTemplateFromBuiltinResponse = z.infer<
  typeof CreateEmailTemplateFromBuiltinResponseSchema
>;

export type ListEmailTemplateVersionsInput = z.infer<
  typeof ListEmailTemplateVersionsInputSchema
>;
export type ListEmailTemplateVersionsResponse = z.infer<
  typeof ListEmailTemplateVersionsResponseSchema
>;

export type CreateEmailTemplateDraftInput = z.infer<
  typeof CreateEmailTemplateDraftInputSchema
>;
export type CreateEmailTemplateDraftResponse = z.infer<
  typeof CreateEmailTemplateDraftResponseSchema
>;

export type UpdateEmailTemplateDraftInput = z.infer<
  typeof UpdateEmailTemplateDraftInputSchema
>;
export type UpdateEmailTemplateDraftResponse = z.infer<
  typeof UpdateEmailTemplateDraftResponseSchema
>;

export type FinalizeEmailTemplateDraftInput = z.infer<
  typeof FinalizeEmailTemplateDraftInputSchema
>;
export type FinalizeEmailTemplateDraftResponse = z.infer<
  typeof FinalizeEmailTemplateDraftResponseSchema
>;

export type PublishEmailTemplateVersionInput = z.infer<
  typeof PublishEmailTemplateVersionInputSchema
>;
export type PublishEmailTemplateVersionResponse = z.infer<
  typeof PublishEmailTemplateVersionResponseSchema
>;
